import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  MousePointerClick,
  CalendarClock,
  Plus,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  Trash2,
  Pencil,
  Sparkles,
  Bell,
  FolderOpen,
  Folder,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import Navbar from "@/components/Navbar";
import CampaignModal from "@/components/CampaignModal";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/context/BillingContext";

function KpiCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-500/30 hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            {label}
          </div>
          <div className="mt-3 text-4xl font-black tracking-tight font-mono text-slate-900 tabular-nums">
            {value.toLocaleString()}
          </div>
        </div>
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}
        >
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Short link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" /> Copy
        </>
      )}
    </button>
  );
}

export default function Dashboard() {
  const { billing, isPro, refresh: refreshBilling } = useBilling();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [folderFilter, setFolderFilter] = useState("all");
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    try {
      const [{ data: statsData }, { data: campaignData }] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/campaigns"),
      ]);
      setStats(statsData);
      setCampaigns(campaignData);
      refreshBilling?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const chartData = useMemo(
    () =>
      stats?.daily_series?.map((d) => ({
        ...d,
        label: new Date(d.date).toLocaleDateString(undefined, {
          weekday: "short",
        }),
      })) || [],
    [stats]
  );

  const { grouped, folderList } = useMemo(() => {
    const map = {};
    for (const c of campaigns) {
      const key = c.folder || "_uncategorised_";
      (map[key] = map[key] || []).push(c);
    }
    const folders = Object.keys(map).filter((k) => k !== "_uncategorised_").sort();
    return { grouped: map, folderList: folders };
  }, [campaigns]);

  const visibleGroups = useMemo(() => {
    const keys = [...folderList];
    if ((grouped["_uncategorised_"] || []).length > 0) keys.push("_uncategorised_");
    const filtered = folderFilter === "all" ? keys : keys.filter((k) => k === folderFilter);
    return filtered.map((k) => [k, grouped[k] || []]);
  }, [grouped, folderList, folderFilter]);

  const handleSaved = (saved) => {
    // On create (no `editing` set), jump straight to the campaign detail page so
    // the user can see + download their new QR immediately.
    const wasCreate = !editing;
    load();
    if (wasCreate && saved?.id) {
      navigate(`/campaigns/${saved.id}`);
    }
  };

  const openCreate = () => {
    const limit = billing?.campaign_limit;
    if (!isPro && limit != null && (billing?.campaign_count ?? 0) >= limit) {
      toast.error(
        `You've reached the Free plan limit of ${limit} campaigns. Upgrade to Pro to create more.`,
        { duration: 6000 }
      );
      navigate("/pricing");
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/campaigns/${deleteId}`);
      toast.success("Campaign deleted");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600">
              Overview
            </span>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2"
              data-testid="dashboard-heading"
            >
              Campaign command center
            </h1>
            <p className="text-slate-500 mt-1">
              Everything you need to run and measure your dynamic QR codes.
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="create-campaign-button"
            className="h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm hover:shadow"
          >
            <Plus className="w-4 h-4 mr-1" />
            New campaign
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Plan usage banner */}
            {!isPro && billing && (
              <div
                className="mb-6 relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
                data-testid="plan-usage-banner"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white border border-blue-200 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">
                      Free plan · {billing.campaign_count} / {billing.campaign_limit} campaigns used
                    </div>
                    <div className="text-xs text-slate-600">
                      Upgrade to Pro for unlimited campaigns, CSV export, custom domain, and scan alerts.
                    </div>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button
                    className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    data-testid="banner-upgrade-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Upgrade to Pro
                  </Button>
                </Link>
              </div>
            )}
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <KpiCard
                testId="kpi-total-campaigns"
                icon={LayoutGrid}
                label="Total campaigns"
                value={stats?.total_campaigns ?? 0}
                accent="bg-blue-50 text-blue-600"
              />
              <KpiCard
                testId="kpi-total-scans"
                icon={MousePointerClick}
                label="Total scans"
                value={stats?.total_scans ?? 0}
                accent="bg-cyan-50 text-cyan-600"
              />
              <KpiCard
                testId="kpi-scans-this-week"
                icon={CalendarClock}
                label="Scans this week"
                value={stats?.scans_this_week ?? 0}
                accent="bg-violet-50 text-violet-600"
              />
            </div>

            {/* Weekly chart */}
            <div
              className="mt-6 bg-white border border-slate-200 rounded-2xl p-6"
              data-testid="dashboard-weekly-chart"
            >
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Last 7 days
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Scans per day across all campaigns
                  </p>
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Daily scans
                </div>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#2563EB" />
                        <stop offset="100%" stopColor="#06B6D4" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#94A3B8"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#94A3B8"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E2E8F0",
                        fontSize: 12,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="scans"
                      stroke="url(#lineGrad)"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#2563EB" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Campaigns list */}
            <div className="mt-10">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900">Your campaigns</h2>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {campaigns.length} total
                </div>
              </div>

              {campaigns.length === 0 ? (
                <div
                  className="border border-dashed border-slate-300 rounded-2xl bg-white/60 p-14 text-center"
                  data-testid="campaigns-empty-state"
                >
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 mx-auto flex items-center justify-center">
                    <Plus className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900">
                    Create your first campaign
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                    Name it, drop in a destination URL, and we'll generate a
                    QR code you can print anywhere.
                  </p>
                  <Button
                    onClick={openCreate}
                    className="mt-6 h-10 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    data-testid="empty-create-campaign-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> New campaign
                  </Button>
                </div>
              ) : (
                <>
                  {/* Folder filter chips */}
                  {folderList.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="folder-filter-chips">
                      <button
                        onClick={() => setFolderFilter("all")}
                        data-testid="folder-chip-all"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          folderFilter === "all"
                            ? "bg-slate-900 text-white"
                            : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                        All ({campaigns.length})
                      </button>
                      {folderList.map((f) => (
                        <button
                          key={f}
                          onClick={() => setFolderFilter(f)}
                          data-testid={`folder-chip-${f}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            folderFilter === f
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          <Folder className="w-3.5 h-3.5" />
                          {f} ({(grouped[f] || []).length})
                        </button>
                      ))}
                      {(grouped["_uncategorised_"] || []).length > 0 && (
                        <button
                          onClick={() => setFolderFilter("_uncategorised_")}
                          data-testid="folder-chip-uncategorised"
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            folderFilter === "_uncategorised_"
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          Uncategorised ({(grouped["_uncategorised_"] || []).length})
                        </button>
                      )}
                    </div>
                  )}

                  <div className="space-y-6" data-testid="campaigns-groups">
                    {visibleGroups.map(([folderKey, list]) => {
                      if (list.length === 0) return null;
                      const displayName = folderKey === "_uncategorised_" ? "Uncategorised" : folderKey;
                      const isCollapsed = Boolean(collapsed[folderKey]);
                      const showGroupHeader = folderList.length > 0 || folderKey !== "_uncategorised_";
                      return (
                        <div
                          key={folderKey}
                          className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                          data-testid={`folder-group-${folderKey}`}
                        >
                          {showGroupHeader && (
                            <button
                              onClick={() =>
                                setCollapsed((s) => ({ ...s, [folderKey]: !s[folderKey] }))
                              }
                              className="w-full flex items-center gap-2 px-6 py-3 border-b border-slate-100 bg-slate-50/60 hover:bg-slate-50 transition-colors text-left"
                              data-testid={`folder-toggle-${folderKey}`}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              )}
                              {folderKey === "_uncategorised_" ? (
                                <FolderOpen className="w-4 h-4 text-slate-400" />
                              ) : (
                                <Folder className="w-4 h-4 text-blue-500" />
                              )}
                              <span className="font-bold text-sm text-slate-900">{displayName}</span>
                              <span className="text-xs font-semibold text-slate-400">
                                {list.length} {list.length === 1 ? "campaign" : "campaigns"}
                              </span>
                            </button>
                          )}
                          {!isCollapsed && (
                            <div className="w-full overflow-x-auto">
                              <table className="w-full" data-testid="campaigns-table">
                                <thead>
                                  <tr className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                    <th className="px-6 py-3 text-left">Campaign</th>
                                    <th className="px-6 py-3 text-left">Short link</th>
                                    <th className="px-6 py-3 text-left">Destination</th>
                                    <th className="px-6 py-3 text-right">Scans</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {list.map((c) => (
                                    <tr
                                      key={c.id}
                                      data-testid={`campaign-row-${c.shortcode}`}
                                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors"
                                    >
                                      <td className="px-6 py-4">
                                        <Link
                                          to={`/campaigns/${c.id}`}
                                          className="font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                                          data-testid={`campaign-name-link-${c.shortcode}`}
                                        >
                                          {c.name}
                                        </Link>
                                        <div className="text-xs text-slate-400 mt-0.5">
                                          Created {new Date(c.created_at).toLocaleDateString()}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                          <code className="font-mono text-sm font-semibold text-blue-600">
                                            /{c.shortcode}
                                          </code>
                                          <CopyButton
                                            text={c.short_url}
                                            testId={`copy-shortlink-button-${c.shortcode}`}
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 max-w-[240px]">
                                        <a
                                          href={c.destination_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-sm text-slate-600 hover:text-blue-600 truncate inline-flex items-center gap-1.5 max-w-full"
                                        >
                                          <span className="truncate">{c.destination_url}</span>
                                          <ExternalLink className="w-3 h-3 shrink-0" />
                                        </a>
                                      </td>
                                      <td className="px-6 py-4 text-right font-mono font-bold text-slate-900 tabular-nums">
                                        {(c.scan_count || 0).toLocaleString()}
                                      </td>
                                      <td className="px-6 py-4 text-right">
                                        <div className="inline-flex gap-1">
                                          <button
                                            onClick={() => openEdit(c)}
                                            data-testid={`edit-campaign-button-${c.shortcode}`}
                                            className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                            title="Edit"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => setDeleteId(c.id)}
                                            data-testid={`delete-campaign-button-${c.shortcode}`}
                                            className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                            title="Delete"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>

      <CampaignModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaign={editing}
        onSaved={handleSaved}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-campaign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the campaign and its scan history.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
